import { FC, useRef, useState } from "react";
import { DownOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    Project,
    ProjectAnalysis,
    ProjectAnalysisStatus,
    ProjectStatus,
    getAnalysisLabel,
} from "../../../types/ProjectTypes";
import { useClickOutside } from "../../../common/useClickOutside";
import styles from "./AnalysisWidget.module.less";

type AnalysisTag = "pending" | "latest" | "archived";

function getAnalysisTag(analysis: ProjectAnalysis, latestCompletedVersion?: number): AnalysisTag {
    if (analysis.status !== ProjectAnalysisStatus.COMPLETED) return "pending";
    if (latestCompletedVersion != null && analysis.version === latestCompletedVersion) return "latest";
    return "archived";
}

const tagLabels: Record<AnalysisTag, string> = {
    pending: "Pending",
    latest: "Latest",
    archived: "Archived",
};

interface AnalysisWidgetProps {
    project: Project;
    selectedVersion?: number;
    canEdit?: boolean;
    onAnalysisSelect: (version: number) => void;
    onAction?: () => void;
}

export const AnalysisWidget: FC<AnalysisWidgetProps> = ({
    project,
    selectedVersion,
    canEdit,
    onAnalysisSelect,
    onAction,
}) => {
    const { analyses } = project;
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useClickOutside(dropdownRef, open, () => setOpen(false));

    if (project.status === ProjectStatus.DRAFT) {
        return <span>–</span>;
    }

    const selectedAnalysis = analyses.find((a) => a.version === selectedVersion);
    const label = selectedAnalysis ? getAnalysisLabel(selectedAnalysis) : "–";

    const latest = analyses.reduce((a, b) => (a.version > b.version ? a : b), analyses[0]);
    const completedVersions = analyses.filter((a) => a.status === ProjectAnalysisStatus.COMPLETED);
    const latestCompletedVersion = completedVersions.length > 0
        ? Math.max(...completedVersions.map((a) => a.version))
        : undefined;
    const showAction = canEdit && project.status !== ProjectStatus.COMPLETED;
    const isUpdate = latest?.status === ProjectAnalysisStatus.SUBMITTED
        || latest?.status === ProjectAnalysisStatus.REQUIRE_USER_ACTION;
    const actionLabel = isUpdate ? "Update analysis" : "Request new analysis";
    const ActionIcon = isUpdate ? EditOutlined : PlusOutlined;

    return (
        <div className={styles.wrapper} ref={dropdownRef}>
            <button
                className={styles.trigger}
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
            >
                <span>{label}</span>
                <DownOutlined className={styles.chevron} />
            </button>

            {open && (
                <div className={styles.dropdown}>
                    <div className={styles.dropdownTitle}>Analyses</div>
                    <div className={styles.divider} />
                    <div className={styles.analysisList}>
                        {[...analyses].sort((a, b) => b.version - a.version).map((a) => {
                            const tag = getAnalysisTag(a, latestCompletedVersion);
                            const date = a.completedDate || a.submittedDate;
                            return (
                                <button
                                    key={a.version}
                                    className={`${styles.analysisItem} ${a.version === selectedVersion ? styles.analysisItemSelected : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAnalysisSelect(a.version);
                                        setOpen(false);
                                    }}
                                >
                                    <div className={styles.analysisItemContent}>
                                        <div>
                                            <div className={styles.analysisItemLabel}>{getAnalysisLabel(a)}</div>
                                            {date && (
                                                <div className={styles.analysisItemDate}>
                                                    {moment(date).format("MMM D, YYYY h:mm A")}
                                                </div>
                                            )}
                                        </div>
                                        <span className={`${styles.analysisTag} ${styles[`analysisTag--${tag}`]}`}>
                                            {tagLabels[tag]}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    {showAction && onAction && (
                        <>
                            <div className={styles.divider} />
                            <button
                                className={styles.actionItem}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAction();
                                    setOpen(false);
                                }}
                            >
                                <ActionIcon className={styles.actionIcon} />
                                {actionLabel}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
